import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { IconButton, Skeleton } from '@mui/material';

import { DocumentListItemProps, DocumentListProps } from './types';
import { downloadBlob } from '../../../../helpers/fileDownloadHelper';
import { useDocumentsStore } from '../../../../stores/documentsStore';
import { useContextStore } from '../../../../stores/storeRoot';
import {
  DocumentLink,
  LoaderWrapper,
  StyledDownloadIconWrapper,
  StyledHeaderContainer,
  StyledHeaderTableCell,
  StyledTableCell,
  StyledTableHead,
  StyledTableRow,
  ViewAllWrapper,
  Wrapper,
} from './styles';
import Loader from '../../../components/Loader/Loader';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import { handleDocumentPreview } from '../../../../helpers/filePreviewHelper';
import DownloadIcon from '../../../../assets/icons/DownloadIcon';
import {
  cropConnectApiClient,
  CropConnectCountry,
  CropConnectRoleDefinitions,
  DocumentSnapshotDto,
} from '../../../../services/cropConnectApiClient';
import ActionButton from '../../../components/ActionButton/ActionButton';
import SecurityTrim from '../../../../services/auth/SecurityTrim';
import PDFIcon from '../../../../assets/icons/PDFIcon';
import TooltipTranslationHelper from '../../../../helpers/TooltipTranslationHelper';
import {
  formatDate,
  mapLocaleAccordingToNzRules,
} from '../../../../helpers/localeFormatter';

export const DocumentListItem: React.FC<DocumentListItemProps> = ({
  countryIsoCode,
  id,
  title,
  invoiceNumber,
  campaignYear,
  creationDate,
  read,
  showInvoiceNumber,
  showCalendarYear,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isRead, setIsRead] = useState(read);

  const { nordzuckerInternalUser } = useContextStore();

  const renderText = (text?: string | number) => {
    const displayedText = text !== undefined && text !== null ? text : '-';
    return isRead ? <span>{displayedText}</span> : <b>{displayedText}</b>;
  };

  const handlePreviewClick = () => {
    setIsPreviewLoading(true);
    if (!nordzuckerInternalUser) {
      setIsRead(true);
    }
    handleDocumentPreview(id!).finally(() => {
      setIsPreviewLoading(false);
    });
  };

  const renderDownloadCell = () => {
    if (isDownloading) {
      return (
        <TooltipTranslationHelper
          titleKey={TranslationKeys.documents_list_download_document}
          placement="top"
        >
          <LoaderWrapper>
            <Loader isFullScreen={false} size={40} />
          </LoaderWrapper>
        </TooltipTranslationHelper>
      );
    } else {
      return (
        <TooltipTranslationHelper
          titleKey={TranslationKeys.documents_list_download_document}
          placement="top"
        >
          <IconButton
            edge="end"
            aria-label="download"
            onClick={() => {
              const alreadySet = isDownloading;
              setIsDownloading(true);
              if (!alreadySet) {
                cropConnectApiClient
                  .downloadDocument(id!)
                  .then(async (r) => {
                    if (!nordzuckerInternalUser) {
                      setIsRead(true);
                    }
                    downloadBlob(id!, r.data);
                  })
                  .catch((r) => {
                    console.error(
                      `Error downloading document: ${JSON.stringify(r)}`
                    );
                  })
                  .finally(() => {
                    setIsDownloading(false);
                  });
              }
            }}
          >
            <StyledDownloadIconWrapper>
              <DownloadIcon />
            </StyledDownloadIconWrapper>
          </IconButton>
        </TooltipTranslationHelper>
      );
    }
  };

  return (
    <StyledTableRow key={id}>
      <StyledTableCell align="left">
        <TooltipTranslationHelper
          titleKey={TranslationKeys.documents_list_preview_document}
          placement="top"
        >
          {isPreviewLoading ? (
            <LoaderWrapper>
              <Loader isFullScreen={false} size={40} />
            </LoaderWrapper>
          ) : (
            <DocumentLink onClick={handlePreviewClick}>
              <IconButton>
                <PDFIcon />
              </IconButton>
              {renderText(title)}
            </DocumentLink>
          )}
        </TooltipTranslationHelper>
      </StyledTableCell>
      {showInvoiceNumber && (
        <StyledTableCell align="center">
          {renderText(invoiceNumber)}
        </StyledTableCell>
      )}
      {showCalendarYear && (
        <StyledTableCell align="center">
          {renderText(campaignYear)}
        </StyledTableCell>
      )}
      <StyledTableCell align="center">
        {renderText(
          formatDate(creationDate!, mapLocaleAccordingToNzRules(countryIsoCode))
        )}
      </StyledTableCell>
      <StyledTableCell>{renderDownloadCell()}</StyledTableCell>
    </StyledTableRow>
  );
};

// Phil, 30.8.24: If we need to show dynamic header texts, we should receive them by the backend and display them accordingly here :).
export const DocumentListHeader: React.FC<{
  title?: string;
  showInvoiceNumber: boolean;
  onFullReportClick?: () => void;
  buttonText?: string | ReactNode;
  showExtraButton?: boolean;
  showCalendarYear?: boolean;
}> = ({
  title,
  showInvoiceNumber,
  onFullReportClick,
  buttonText,
  showExtraButton,
  showCalendarYear,
}) => {
  const { t } = useI18n();

  return (
    <>
      <StyledHeaderContainer>
        <h2>{title ? title : t(TranslationKeys.documents_list_title)}</h2>
        {showExtraButton && (
          <SecurityTrim
            requiredBusinessPartnerRoles={[
              CropConnectRoleDefinitions.Grower_DE,
              CropConnectRoleDefinitions.Grower_Organic_DE,
            ]}
          >
            <ActionButton
              variant="accept-blue"
              buttonText={buttonText}
              onClickAction={onFullReportClick ?? (() => {})}
            />
          </SecurityTrim>
        )}
      </StyledHeaderContainer>
      <StyledTableHead>
        <StyledTableRow key="header">
          <StyledHeaderTableCell align="left">
            {t(TranslationKeys.documents_list_column_header_filename)}
          </StyledHeaderTableCell>
          {showInvoiceNumber && (
            <StyledHeaderTableCell align="center">
              {t(TranslationKeys.document_list_column_header_invoiceId)}
            </StyledHeaderTableCell>
          )}
          {showCalendarYear && (
            <StyledHeaderTableCell align="center">
              {t(TranslationKeys.documents_list_column_header_businessYear)}
            </StyledHeaderTableCell>
          )}
          <StyledHeaderTableCell align="center">
            {t(TranslationKeys.documents_list_column_header_creationDate)}
          </StyledHeaderTableCell>
          <StyledHeaderTableCell>
            {t(TranslationKeys.documents_list_column_header_actions)}
          </StyledHeaderTableCell>
        </StyledTableRow>
      </StyledTableHead>
    </>
  );
};

const DocumentList: React.FC<DocumentListProps> = ({
  title,
  folder,
  pageSize,
  onToggleView,
  buttonText,
  showExtraButton,
  hideInvoiceColumn,
  postingDateInsteadOfCreationDate = false,
  hideCalendarYearColumn,
  prefilter,
}) => {
  const initialPageSize = pageSize ?? 10;
  const [documents, setDocuments] = useState<DocumentSnapshotDto[]>([]);
  const [showInvoiceNumberForCountry, setShowInvoiceNumberForCountry] =
    useState<boolean>(false);
  const [showCalendarYear, setShowCalendarYear] = useState<boolean>(false);
  const [totalElementsCount, setTotalElementsCount] = useState(0);
  const { isUpdating, isContextUpdating, isInitializing, getDocumentsPage } =
    useDocumentsStore();
  const { activeBpNumber, activeBpNumberCountry } = useContextStore();
  const { t } = useI18n();

  useEffect(() => {
    loadDocumentsPageIntern(0, initialPageSize);
  }, [activeBpNumber, initialPageSize]);

  useMemo(() => {
    setShowInvoiceNumberForCountry(
      !(
        hideInvoiceColumn ||
        activeBpNumberCountry?.toUpperCase() === CropConnectCountry.DE
      )
    );
    setShowCalendarYear(!hideCalendarYearColumn);
  }, [activeBpNumberCountry, hideInvoiceColumn, showCalendarYear]);

  const loadDocumentsPageIntern = async (
    pageIndex: number,
    pageSize: number
  ) => {
    try {
      const retrievalResult = await getDocumentsPage(
        folder,
        pageSize,
        pageIndex * pageSize,
        postingDateInsteadOfCreationDate ? 'postingDate' : 'creationDate',
        true
      );
      if (!retrievalResult?.documents) {
        console.error('No documents returned from API');
      }
      setDocuments(retrievalResult?.documents ?? []);
      setTotalElementsCount(retrievalResult?.availableItemsCount ?? 0);
    } catch (e) {
      console.error('Error loading documents:', e);
      setDocuments([]);
      setTotalElementsCount(0);
    }
  };

  const handleViewAll = () => {
    const realisticPageSize = totalElementsCount || 1000;
    loadDocumentsPageIntern(0, realisticPageSize);
  };

  const renderItemRow = (item: DocumentSnapshotDto) => (
    <DocumentListItem
      key={item.id}
      id={item.id}
      invoiceNumber={
        item.additionalData ? item.additionalData!['InvoiceNumber'] : undefined
      }
      countryIsoCode={activeBpNumberCountry}
      title={item.name}
      campaignYear={item.campaignYear}
      creationDate={
        postingDateInsteadOfCreationDate ? item.postingDate : item.creationDate
      }
      read={item.read}
      showInvoiceNumber={showInvoiceNumberForCountry}
      showCalendarYear={showCalendarYear}
    />
  );

  const renderHeaderRow = () => (
    <DocumentListHeader
      title={title}
      showInvoiceNumber={showInvoiceNumberForCountry}
      showCalendarYear={showCalendarYear}
      onFullReportClick={onToggleView}
      buttonText={buttonText}
      showExtraButton={showExtraButton}
    />
  );

  return (
    <Wrapper>
      {isUpdating || isContextUpdating || isInitializing ? (
        <div className="skeleton-table">
          <Skeleton
            variant="rectangular"
            width="100%"
            height={50}
            animation="wave"
            style={{ borderRadius: '8px' }}
          />
          {Array.from({ length: 10 }).map((_, index) => (
            <div key={index} className="skeleton-row">
              <Skeleton
                variant="rectangular"
                width="60%"
                height={40}
                animation="wave"
                style={{ borderRadius: '8px' }}
              />
              <Skeleton
                variant="rectangular"
                width="60%"
                height={40}
                animation="wave"
                style={{ borderRadius: '8px' }}
              />
              <Skeleton
                variant="rectangular"
                width="60%"
                height={40}
                animation="wave"
                style={{ borderRadius: '8px' }}
              />
              <Skeleton
                variant="rectangular"
                width="90%"
                height={40}
                animation="wave"
                style={{ borderRadius: '8px' }}
              />
            </div>
          ))}
        </div>
      ) : documents.length === 0 ? (
        <>
          <StyledHeaderContainer>
            <h3>{t(TranslationKeys.my_payments_documents_list_title)}</h3>
            {showExtraButton && (
              <SecurityTrim
                requiredBusinessPartnerRoles={[
                  CropConnectRoleDefinitions.Grower_DE,
                  CropConnectRoleDefinitions.ServiceProvider_DE,
                  CropConnectRoleDefinitions.Grower_Organic_DE,
                ]}
              >
                <ActionButton
                  variant="accept-blue"
                  buttonText={buttonText}
                  onClickAction={onToggleView}
                />
              </SecurityTrim>
            )}
          </StyledHeaderContainer>
          <p>{t(TranslationKeys.documents_list_no_documents_found)}</p>
        </>
      ) : (
        <>
          {renderHeaderRow()}
          {documents
            .filter((document) =>
              document.name
                ?.toLowerCase()
                .includes(prefilter?.toLowerCase() ?? '')
            )
            .map(renderItemRow)}
          {documents.length < totalElementsCount && (
            <ViewAllWrapper>
              <ActionButton
                variant="accept-blue-light"
                buttonText={t(TranslationKeys.documents_list_button_view_all)}
                onClickAction={handleViewAll}
              />
            </ViewAllWrapper>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default DocumentList;
